import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { Metamodel } from 'app/model/base/model';
import { AzureTableSink } from 'app/model/pipeline/copysink/azure-table.copysink';
import { AzureTableCopySource } from 'app/model/pipeline/copysource/azure-table.copysource';
import { azureStorageAuthTypes, ConnectionAuthKind, CredentialTypeOptions } from 'shell-models-lib';
import { storageEndpointSetting, sasUriSetting } from 'app/registries/linked-service/azure-storage-shared-fields';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { storagePEWellknownErrorMessages } from 'app/shared/utils/connector-metadata-static-consts';
import { accountKeyConnectionStringSetting } from '../consts/metadata-consts-with-ec-deps';
import { BuiltInDataSourceOptionId, DataSourceOptionKind } from '@powerquery/mashup-editor-client';
import { combinedMIAuthenticationOption } from '../utls/authenticationUtils';
import { GlobalVariables } from 'app/services/globals';
import { compact } from 'lodash';

export const azureTableStorageMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {

    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.azureTable.linkedServiceType,
        displayName: stringResources.AzureTableStorage_DisplayText,
        linkedServiceKind: 'table',
        supportedInTrident: true,
        supportCopyJobSource: true,
        supportCopyJobSink: GlobalVariables.copyJobAzureTableSink,
        supportedJobModesAsSource: GlobalVariables.copyJobIncrementalCopyPhase2 ? undefined : ['Batch'],
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        supportOPDG: GlobalVariables.enableOPDG,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.BuiltIn,
            id: BuiltInDataSourceOptionId.AzureTables
        },
        supportedAuthKindsInTrident: compact([
            ConnectionAuthKind.Key,
            ConnectionAuthKind.WorkspaceIdentity,
            ConnectionAuthKind.OAuth2
        ]),
        icon: {
            className: 'svg-i-100x100-AzureTable'
        },
        category: ['Azure'],
        copySinkTypes: [Metamodel.modelFor(AzureTableSink).name],
        copySourceTypes: [Metamodel.modelFor(AzureTableCopySource).name],
        isAzureTable: true,
        supportPE: true,
        peGroupIDs: ['table'],
        peWellknowErrorMessages: storagePEWellknownErrorMessages,
        resourceTypes: ['Microsoft.ClassicStorage/storageAccounts', 'Microsoft.Storage/storageAccounts'],
        queryAlias: 'azureTableSourceQuery',
        isFirstClass: true,
        supportDatasetV2: true,
        onetimeQueryTemplate: '',
        supportFirstClassParameterExperience: true,
        // eslint-disable-next-line max-len
        timedQueryTemplate: '<<columnName>> gt datetime\'@{formatDateTime(pipeline().parameters.windowStart, \'yyyy-MM-ddTHH:mmZ\' )}\' \nand <<columnName>> le datetime\'@{formatDateTime(pipeline().parameters.windowEnd, \'yyyy-MM-ddTHH:mmZ\' )}\'',
        deltaLoadQueryTemplate: `<<columnName>> gt @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue,
        concat('datetime''',formatDateTime(json(item().DataLoadingBehaviorSettings).watermarkColumnStartValue, 'yyyy-MM-ddTHH:mmZ'), ''''))}
        and <<columnName>> le @{if(contains(json(item().DataLoadingBehaviorSettings).watermarkColumnType, 'Int'),
        activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue,
        concat('datetime''',formatDateTime(activity('GetMaxWatermarkValue').output.firstRow.CurrentMaxWaterMarkColumnValue, 'yyyy-MM-ddTHH:mmZ'), ''''))}`,
        controls: [
            {
                type: 'select-switch',
                bind: 'authenticationMethod',
                label: stringResources.Authentication_Method_Label,
                options: [
                    {
                        label: stringResources.AuthenticationType_AccountKey,
                        value: azureStorageAuthTypes.AccountKeyAuthentication
                    },
                    {
                        label: stringResources.SasUri,
                        value: azureStorageAuthTypes.SasUriAuthentication
                    },
                ].concat(combinedMIAuthenticationOption),
                isLabelVertical: true,
                defaultValue: 'AccountKeyAuthentication',
                cases: [
                    {
                        caseValue: azureStorageAuthTypes.AccountKeyAuthentication,
                        children: [
                            accountKeyConnectionStringSetting('table', false, true)
                        ]
                    },
                    {
                        caseValue: azureStorageAuthTypes.SasUriAuthentication,
                        children: [
                            sasUriSetting(true, false, true)
                        ]
                    },
                    {
                        caseValue: azureStorageAuthTypes.MSIAuthentication,
                        children: [
                            storageEndpointSetting('table'),
                            {
                                type: 'management-identity-info',
                                resourceType: stringResources.AzureTableStorage_DisplayText,
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-table-storage?tabs=data-factory#system-assigned-managed-identity-authentication'
                            }
                        ]
                    },
                    {
                        // for UAMI there won't be any radio buttons, since inline mode is not supported.
                        caseValue: azureStorageAuthTypes.UserAssignedManagedIdentity,
                        children: [
                            storageEndpointSetting('table'),
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }]
                            }
                        ]
                    }
                ]
            }
        ]
    };
    return metadata;
};
